import React from "react";
import Image from "next/image";
import { ContentCard, Blocks } from "@/types/cms";
import { renderRichTextWithMutedStrikethrough } from "@/lib/richText";
import { getCmsImageUrl } from "@/lib/cmsHelpers";
import styles from "./TestimonialCard.module.css";

interface TestimonialCardProps {
  card: ContentCard;
  body?: Blocks | string;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ card, body }) => {
  const blocksFromCard = Array.isArray(card.content)
    ? card.content
    : Array.isArray(card.body)
      ? card.body
      : undefined;
  const overrideBlocks = Array.isArray(body) ? (body as Blocks) : undefined;
  const blocks = overrideBlocks || blocksFromCard;

  const h1Block = blocks?.find((b: any) => b.type === "heading" && b.level === 1);
  const h2Block = blocks?.find((b: any) => b.type === "heading" && b.level === 2);
  const quoteBlocks = blocks?.filter(
    (b: any) =>
      b.type === "paragraph" ||
      (b.type === "heading" && b.level !== 1 && b.level !== 2)
  );

  const personName = h1Block?.children?.map((c: any) => c.text).join("") || "";
  const jobTitle = h2Block?.children?.map((c: any) => c.text).join("") || "";

  // Get image for avatar — routed through `getCmsImageUrl` so every
  // CMS response shape is normalized and the URL gets Cloudinary's
  // `q_auto:good,f_auto,w_...,c_scale` transforms applied before `<Image>`
  // re-optimizes it to WebP/AVIF.
  const avatarUrl =
    getCmsImageUrl((card as any).imageDesktop) ||
    getCmsImageUrl((card as any).image) ||
    getCmsImageUrl((card as any).imageMobile);

  return (
    <div className={styles.card}>
      <div className={styles.quote}>
        {quoteBlocks ? renderRichTextWithMutedStrikethrough(quoteBlocks) : null}
      </div>
      {(avatarUrl || personName || jobTitle) && (
        <div className={styles.metaRow}>
          {avatarUrl && (
            <Image
              src={avatarUrl}
              alt={personName || "Avatar"}
              width={70}
              height={70}
              sizes="70px"
              className={styles.avatar}
            />
          )}
          <div className={styles.metaText}>
            {personName && <p className={styles.name}>{personName}</p>}
            {jobTitle && <p className={styles.title}>{jobTitle}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialCard;
